/**
 * Recruitment module permissions (2026-10-19). Client-safe: no DB access.
 *
 * The module has three views, each with its own View / Edit switch in
 * Settings > Roles (and in the per-user grants of Settings > User Access):
 *
 *  - recruit_view / recruit_edit          Recruit (the candidate pipeline)
 *  - philippines_view / philippines_edit  Philippines (intake inbox + PH funnel)
 *  - analytics_view                       Recruitment Analytics (read-only, so no edit)
 *
 * `recruit_edit` is the master switch of the Recruit write keys
 * (add_candidates, schedule_interviews, record_outcomes, add_notes,
 * drop_candidates, enroll_training, no_show, undo_employee): a write key only
 * counts when Recruit Edit is on.
 *
 * `view_all_candidates` decides which candidates a user sees. On (Human
 * Resources and the other roles that run the pipeline) = every candidate.
 * Off = only the candidates with an interview assigned to the user, the ones
 * they have to approve / deny or comment on (interviewer id or name on
 * recruitment_interviews; see recruitment-scope.ts). Super Admin always sees
 * everyone.
 *
 * Legacy fallbacks (roles saved before the switches existed, and the per-user
 * grants that carry the old `client_care_interviewer` flag) keep working until
 * the role is saved again or `migrations/recruitment_view_permissions_2026_10_19.sql`
 * stamps the new keys:
 *  - recruit_view         <- view
 *  - recruit_edit         <- any write key
 *  - philippines_view     <- view, unless client_care_interviewer
 *  - philippines_edit     <- add_candidates
 *  - analytics_view       <- view_analytics (old key), unless client_care_interviewer
 *  - view_all_candidates  <- NOT client_care_interviewer
 */

export const RECRUITMENT_VIEWS = ['recruit', 'philippines', 'analytics'] as const;
export type RecruitmentView = (typeof RECRUITMENT_VIEWS)[number];

export const RECRUITMENT_VIEW_LABELS: Record<RecruitmentView, string> = {
  recruit: 'Recruit (candidate pipeline)',
  philippines: 'Recruitment Philippines (intake inbox + PH funnel)',
  analytics: 'Recruitment Analytics',
};

export const RECRUITMENT_VIEW_ROUTES: Record<RecruitmentView, string> = {
  recruit: '/recruitment/recruit',
  philippines: '/recruitment/philippines',
  analytics: '/recruitment/analytics',
};

/** Views that have something to edit (Analytics is read-only). */
export const RECRUITMENT_EDITABLE_VIEWS: readonly RecruitmentView[] = ['recruit', 'philippines'];

export function recruitmentViewKey(view: RecruitmentView, action: 'view' | 'edit'): string {
  return `${view}_${action}`;
}

/** Recruit write keys, all under the Recruit Edit switch. */
export const RECRUITMENT_WRITE_KEYS = [
  'add_candidates',
  'schedule_interviews',
  'record_outcomes',
  'add_notes',
  'drop_candidates',
  'enroll_training',
  'no_show',
  'undo_employee',
] as const;
export type RecruitmentWriteKey = (typeof RECRUITMENT_WRITE_KEYS)[number];

/** Every key the editors offer (view / edit per view, scope, writes, extras). */
export const RECRUITMENT_PERMISSION_KEYS = [
  'view',
  'view_all_candidates',
  'recruit_view',
  'recruit_edit',
  'philippines_view',
  'philippines_edit',
  'analytics_view',
  ...RECRUITMENT_WRITE_KEYS,
  'weekly_briefing',
  'manage_job_applications',
] as const;

/** Legacy restrictive flag of the Client Care / Marketing / Revo QA interviewers (2026-09-28). */
export const LEGACY_SCOPED_INTERVIEWER_KEY = 'client_care_interviewer';

/** Old name of analytics_view. */
export const LEGACY_ANALYTICS_KEY = 'view_analytics';

type ModuleShape = Record<string, unknown>;

function flag(mp: ModuleShape, key: string): boolean | undefined {
  const v = mp[key];
  return typeof v === 'boolean' ? v : undefined;
}

function legacyScoped(mp: ModuleShape): boolean {
  return mp[LEGACY_SCOPED_INTERVIEWER_KEY] === true;
}

function anyWriteKey(mp: ModuleShape): boolean {
  return RECRUITMENT_WRITE_KEYS.some((k) => mp[k] === true);
}

/**
 * Resolve one recruitment permission on an enabled module object, with the
 * legacy fallbacks above. `mp` is `permissions.modules.recruitment` (an object;
 * callers handle `false` / undefined = module off).
 */
export function resolveRecruitmentPermission(mp: ModuleShape, permission: string): boolean {
  switch (permission) {
    case 'recruit_view':
      return flag(mp, 'recruit_view') ?? mp.view === true;
    case 'recruit_edit':
      return flag(mp, 'recruit_edit') ?? anyWriteKey(mp);
    case 'philippines_view':
      return flag(mp, 'philippines_view') ?? (mp.view === true && !legacyScoped(mp));
    case 'philippines_edit':
      return flag(mp, 'philippines_edit') ?? mp.add_candidates === true;
    case 'analytics_view':
    case LEGACY_ANALYTICS_KEY:
      return (
        flag(mp, 'analytics_view') ??
        (mp[LEGACY_ANALYTICS_KEY] === true && !legacyScoped(mp))
      );
    case 'view_all_candidates':
      return flag(mp, 'view_all_candidates') ?? !legacyScoped(mp);
    default:
      if ((RECRUITMENT_WRITE_KEYS as readonly string[]).includes(permission)) {
        return mp[permission] === true && resolveRecruitmentPermission(mp, 'recruit_edit');
      }
      return mp[permission] === true;
  }
}

/**
 * True when the user only sees the candidates with an interview assigned to
 * them (recruitment.view_all_candidates off). Never Super Admin: the SA loop
 * sets every key true, and SA is never scoped.
 * `modules` is UserPermissions.modules (server or PermissionsProvider).
 */
export function isAssignedInterviewerScope(
  roleName: string | null | undefined,
  modules: Record<string, unknown> | null | undefined,
): boolean {
  if (roleName === 'Super Admin') return false;
  const recruitment = modules?.recruitment;
  if (!recruitment || typeof recruitment !== 'object') return false;
  return !resolveRecruitmentPermission(recruitment as ModuleShape, 'view_all_candidates');
}

/** Views the user may open (for the landing page cards and the sidebar). */
export function visibleRecruitmentViews(
  roleName: string | null | undefined,
  modules: Record<string, unknown> | null | undefined,
): RecruitmentView[] {
  const recruitment = modules?.recruitment;
  if (recruitment === false || recruitment == null || typeof recruitment !== 'object') return [];
  if (roleName === 'Super Admin') return [...RECRUITMENT_VIEWS];
  const mp = recruitment as ModuleShape;
  return RECRUITMENT_VIEWS.filter((view) =>
    resolveRecruitmentPermission(mp, recruitmentViewKey(view, 'view')),
  );
}

/** Keys a freshly enabled role gets in the Roles editor: Recruit view only, nothing else. */
export function defaultRecruitmentModulePermissions(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const key of RECRUITMENT_PERMISSION_KEYS) out[key] = false;
  out.view = true;
  out.recruit_view = true;
  return out;
}

/**
 * Keep a role's recruitment keys consistent after one switch changes
 * (Roles editor): a view off turns its edit off, Recruit Edit off clears the
 * write keys, the module off clears everything.
 */
export function cascadeRecruitmentPermissionChange(
  mp: Record<string, unknown>,
  key: string,
  value: boolean,
): void {
  mp[key] = value;
  if (value) return;
  if (key === 'view') {
    for (const k of RECRUITMENT_PERMISSION_KEYS) if (k !== 'view') mp[k] = false;
    return;
  }
  if (key === 'recruit_view') mp.recruit_edit = false;
  if (key === 'recruit_view' || key === 'recruit_edit') {
    for (const k of RECRUITMENT_WRITE_KEYS) mp[k] = false;
  }
  if (key === 'philippines_view') mp.philippines_edit = false;
}
